import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import Newsletter from "@/components/Newsletter";
import { api } from "@/lib/api";

const CATEGORIES = ["ALL", "TEES", "TOPS", "BOTTOMS", "OUTERWEAR", "ACCESSORIES", "SHOES"];

export default function Shop() {
  const [category, setCategory] = useState("ALL");
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.getProducts().then(setProducts).catch(() => {});
  }, []);

  const visible = products.filter((p) => category === "ALL" || p.category === category);

  return (
    <div data-testid="shop-page">
      <PageHeader overline="THE CATALOG" title="Shop" sub="Every piece is designed as part of a complete outfit. New drops added as they're ready — never rushed." />

      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 py-14">
        <div className="flex flex-wrap gap-2" data-testid="shop-category-filters">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              data-testid={`shop-filter-${c.toLowerCase()}`}
              onClick={() => setCategory(c)}
              className={`border px-5 py-2.5 font-mono text-[10px] tracking-[0.3em] transition-colors ${
                category === c ? "border-white bg-white text-black" : "border-wire text-neutral-400 hover:border-neutral-500 hover:text-white"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {visible.length > 0 ? (
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" data-testid="shop-product-grid">
            {visible.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.06} className="bg-obsidian border border-wire -mt-px -ml-px">
                <Link to={`/product/${p.slug}`} data-testid={`product-card-${p.slug}`} className="group block p-6 sm:p-8">
                  <div className="relative overflow-hidden border border-wire bg-charcoal">
                    <img src={p.images?.[0]} alt={p.name}
                      className={`aspect-[4/5] w-full object-cover transition-opacity duration-500 ${p.status === "live" && p.images?.[1] ? "group-hover:opacity-0" : ""} ${p.status !== "live" ? "opacity-40 grayscale" : ""}`} />
                    {p.status === "live" && p.images?.[1] && (
                      <img src={p.images[1]} alt={`${p.name} alternate view`}
                        className="absolute inset-0 aspect-[4/5] w-full object-cover opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                    )}
                    <span className="absolute left-3 top-3 border border-wire bg-obsidian/80 px-3 py-1 font-mono text-[9px] tracking-[0.3em] text-neutral-300 backdrop-blur">
                      {p.category}
                    </span>
                    {p.members_only && (
                      <span className="absolute right-3 top-3 border border-white/40 bg-obsidian/80 px-3 py-1 font-mono text-[9px] tracking-[0.3em] text-white backdrop-blur" data-testid={`members-badge-${p.slug}`}>
                        MEMBERS FIRST
                      </span>
                    )}
                    {p.status !== "live" && (
                      <span className="absolute inset-0 flex items-center justify-center font-mono text-[10px] tracking-[0.4em] text-neutral-200" data-testid={`coming-soon-badge-${p.slug}`}>
                        {p.status === "sold_out" ? "SOLD OUT" : "COMING SOON"}
                      </span>
                    )}
                  </div>
                  <div className="mt-6 flex items-start justify-between gap-4">
                    <div>
                      <p className={`font-display text-base font-bold uppercase tracking-wide ${p.status === "live" ? "text-white" : "text-neutral-500"}`}>{p.name}</p>
                      <p className="mt-1 font-mono text-[10px] tracking-[0.25em] text-neutral-500">
                        {(p.sizes || []).join(" — ")} {p.status === "live" ? "/ UNISEX" : ""}
                        {p.status === "coming_soon" && p.drop_date ? ` — DROPS ${new Date(p.drop_date).toLocaleDateString("en-US", { month: "short", day: "numeric" }).toUpperCase()}` : ""}
                      </p>
                      {p.status === "live" && p.stock && (
                        <p className="mt-1 font-mono text-[10px] tracking-[0.25em] text-neutral-400" data-testid={`stock-badge-${p.slug}`}>
                          {p.stock_total} / {p.run_size || p.stock_total} LEFT
                        </p>
                      )}
                    </div>
                    <p className="font-script text-xl text-neutral-200" data-testid={`product-price-${p.slug}`}>${Number(p.price).toFixed(2)}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        ) : (
          <div className="mt-12 border border-wire px-8 py-24 text-center" data-testid="shop-empty-category">
            <p className="font-display text-2xl font-extrabold uppercase tracking-tight text-neutral-500">Coming Soon</p>
            <p className="mt-4 font-mono text-xs tracking-[0.25em] text-neutral-600">
              {category} ARE IN THE WORKS. THE OUTFIT COMES FIRST — NEVER RUSHED.
            </p>
            <button data-testid="shop-empty-back" onClick={() => setCategory("ALL")}
              className="mt-8 inline-block border border-wire px-8 py-3 font-mono text-[10px] tracking-[0.3em] text-neutral-300 transition-colors hover:border-white hover:text-white">
              BACK TO ALL
            </button>
          </div>
        )}
      </div>
      <Newsletter />
    </div>
  );
}
